import React, { FC, Fragment, useState } from 'react';
import { Button, ListItemText, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DeliveryNoteSelection } from 'models/delivery-schedule';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

type DeliverySchedulePrintMenuProps = {
    printing: boolean;
    // The list PDF is always the planned list, so it is only offered while the table shows it.
    isListAvailable: boolean;
    onPrintList: () => void;
    onPrintDeliveryNotes: (selection: DeliveryNoteSelection) => void;
};

const MENU_ID = 'delivery-schedule-print-menu';

// DD-3546: the menu sits inside the accordion summary, so every click stops propagation
// to keep the accordion from toggling.
const DeliverySchedulePrintMenu: FC<DeliverySchedulePrintMenuProps> = ({
    printing,
    isListAvailable,
    onPrintList,
    onPrintDeliveryNotes,
}: DeliverySchedulePrintMenuProps) => {
    const { t: translate } = useTranslation(['delivery-schedule', 'orders']);
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
    const open = !!anchorElement;
    // External technicians never get the delivery notes, as in the Order List batch print.
    const showDeliveryNotes = !permissions.isExternalTechnician(currentUserVar().role);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorElement(event.currentTarget);
    };

    const handleClose = (event?: React.SyntheticEvent) => {
        event?.stopPropagation();
        setAnchorElement(null);
    };

    const handleSelect = (event: React.SyntheticEvent, print: () => void) => {
        handleClose(event);
        print();
    };

    return (
        <Fragment>
            <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                size="medium"
                disabled={printing}
                aria-controls={open ? MENU_ID : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleOpen}
                startIcon={<PictureAsPdfIcon />}
                endIcon={<ArrowDropDownIcon />}
            >
                {translate('orders:PRINT')}
            </Button>
            <Menu
                sx={{ marginTop: 1 }}
                id={MENU_ID}
                anchorEl={anchorElement}
                open={open}
                onClose={(event: React.SyntheticEvent) => handleClose(event)}
                onClick={(event: React.SyntheticEvent) => event.stopPropagation()}
            >
                <MenuItem disabled={!isListAvailable} onClick={(event) => handleSelect(event, onPrintList)}>
                    <ListItemText
                        primary={translate('delivery-schedule:PRINT_LIST')}
                        secondary={
                            isListAvailable ? undefined : translate('delivery-schedule:PRINT_LIST_NOT_FINISHED_ONLY')
                        }
                    />
                </MenuItem>
                {showDeliveryNotes && (
                    <MenuItem
                        onClick={(event) =>
                            handleSelect(event, () => onPrintDeliveryNotes(DeliveryNoteSelection.SENT_ON_DATE))
                        }
                    >
                        {translate('delivery-schedule:PRINT_DELIVERY_NOTES_SENT')}
                    </MenuItem>
                )}
                {showDeliveryNotes && (
                    <MenuItem
                        onClick={(event) =>
                            handleSelect(event, () => onPrintDeliveryNotes(DeliveryNoteSelection.FINISHED_ON_DATE))
                        }
                    >
                        {translate('delivery-schedule:PRINT_DELIVERY_NOTES_FINISHED')}
                    </MenuItem>
                )}
            </Menu>
        </Fragment>
    );
};

export default DeliverySchedulePrintMenu;
